import {useEffect} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {
    HeadlessVaultManager,
    PaymentHandlers,
    PaymentMethodInfo,
    PaymentMethodType,
    Primer,
    PrimerHeadlessCheckout,
} from '@primer-io/checkout-web';
import {useDispatch} from 'react-redux';

import {
    CHECKOUT_PAYMENT_STEP,
    PRIMER_ON_CHECKOUT_COMPLETE,
    PRIMER_ON_CHECKOUT_FAIL,
} from 'constants/analytics/events/checkout';
import {useGetPrimerToken} from 'desktop/pages/checkout/hooks/primer/useGetPrimerToken';
import {getPaymentDeclineCode} from 'libs/checkout/requests/getPaymentDeclineCode';
import {logWithSentry} from 'libs/sentry/sentry';

import {completeOrderReviewResetPending} from '../../store/actions/completeOrder/completeOrder';
import {setIsApplePayAvailable, setVaultedPaymentMethods} from '../../store/reducers/primer/primer';

import {useHandleDecline} from './useHandleDecline';

type PropsType = {
    setOptionPayment: () => void;
    runSavePayment: (args: {token: string; orderId: string}) => void;
    onAvailablePaymentMethodsLoad: (paymentMethods: PaymentMethodInfo[]) => void;
    onCheckoutFail?: PaymentHandlers['onCheckoutFail'];
    onReady?: () => void;
    onPrimerHeadlessReady: (primerHeadless: PrimerHeadlessCheckout) => void;
    skipVaultManager?: boolean;
    onVaultMangerReady?: (vaultManager: HeadlessVaultManager) => void;
    onBeforePaymentCreate?: PaymentHandlers['onBeforePaymentCreate'];
    onPaymentCreationStart?: PaymentHandlers['onPaymentCreationStart'];
};

/**
 * hook for configuration and initialization of Primer
 */
export const useConfigureAndStartPrimer = ({
    onAvailablePaymentMethodsLoad,
    onBeforePaymentCreate,
    onCheckoutFail: onCheckoutFailCb,
    onPaymentCreationStart,
    onPrimerHeadlessReady,
    onReady,
    onVaultMangerReady,
    runSavePayment,
    setOptionPayment,
    skipVaultManager,
}: PropsType) => {
    const {token} = useGetPrimerToken();
    const dispatch = useDispatch();
    const handleDecline = useHandleDecline();
    const onCheckoutFail: PaymentHandlers['onCheckoutFail'] = async (error, {payment}, handler) => {
        if (payment) {
            sendDesktopAnalyticsEvent(PRIMER_ON_CHECKOUT_FAIL, {
                // @ts-expect-error incorrect types from Primer SDK, payment can contain error field
                diagnosticsId: payment.error?.diagnosticsId || error?.diagnosticsId,
                // @ts-expect-error incorrect types from Primer SDK, payment can contain error field
                errorID: payment.error?.errorId || error?.code,
                externalPaymentID: payment.id,
            });

            const response = await getPaymentDeclineCode(payment.id);
            if (response) {
                handleDecline(response);
            } else {
                sendDesktopAnalyticsEvent(
                    CHECKOUT_PAYMENT_STEP,
                    {
                        message: `No reason from backend - internal primer error: ${error.message}`,
                        method: 'Credit card',
                    },
                    false,
                );
            }
        }
        onCheckoutFailCb?.(error, {payment}, handler);
        dispatch(completeOrderReviewResetPending());
    };

    useEffect(() => {
        const initiatePrimer = async (clientToken: string) => {
            try {
                const primerHeadless = await Primer.createHeadless(clientToken, {
                    card: {
                        cardholderName: {
                            required: false,
                        },
                    },
                    onAvailablePaymentMethodsLoad: (paymentMethods: PaymentMethodInfo[]) => {
                        onAvailablePaymentMethodsLoad(paymentMethods);
                        if (
                            paymentMethods.some(
                                method => method.type === PaymentMethodType.APPLE_PAY,
                            )
                        ) {
                            dispatch(setIsApplePayAvailable(true));
                        }
                    },
                    onBeforePaymentCreate,
                    onCheckoutComplete(data) {
                        sendDesktopAnalyticsEvent(PRIMER_ON_CHECKOUT_COMPLETE, {
                            externalPaymentID: data?.payment?.id,
                            orderId: data?.payment?.orderId,
                        });
                        if (!data.payment) return;
                        setOptionPayment();
                        runSavePayment({orderId: data?.payment?.orderId, token: data.payment.id});
                    },
                    onCheckoutFail,
                    onPaymentCreationStart,
                });
                onPrimerHeadlessReady(primerHeadless);

                primerHeadless.start().then(async () => {
                    try {
                        if (skipVaultManager) return;
                        const vaultManager = primerHeadless.createVaultManager();
                        onVaultMangerReady?.(vaultManager);
                        const vaultedMethods = await vaultManager.fetchVaultedPaymentMethods();
                        dispatch(setVaultedPaymentMethods(vaultedMethods));
                    } catch (error) {
                        logWithSentry(error, 'Failed to fetch vaulted payment methods');
                    } finally {
                        onReady?.();
                    }
                });
            } catch (e) {
                logWithSentry(e, 'Failed to initiate primer');
            }
        };

        if (token) {
            initiatePrimer(token);
        }
        // don't fix this eslint disable rule, it will brake
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);
};
